import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, PlusCircle, Scale, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithAuth } from '../utils/api';
import EntityCardGrid from './entities/EntityCardGrid';
import AssetCardGrid from './entities/AssetCardGrid';
import EntityModal from './entities/EntityModal';
import AssetModal from './entities/AssetModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EntityManagementSkeleton = () => (
  <div className="space-y-8 animate-pulse" data-testid="entities-skeleton" aria-busy="true">
    {/* Page header skeleton */}
    <div>
      <div className="h-7 w-56 bg-slate-200 rounded mb-2"></div>
      <div className="h-4 w-96 bg-slate-100 rounded"></div>
    </div>
    {/* Holding Entities section */}
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 bg-emerald-200 rounded"></div>
          <div className="h-6 w-44 bg-slate-200 rounded"></div>
        </div>
        <div className="h-9 w-28 bg-emerald-200 rounded"></div>
      </div>
      <div className="h-14 bg-emerald-50 border border-emerald-100 rounded-lg"></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[0, 1].map((i) => (
          <div key={i} className="bg-slate-100 rounded-lg p-4 h-44 space-y-3">
            <div className="h-5 w-32 bg-slate-200 rounded"></div>
            <div className="h-6 w-28 bg-blue-200 rounded-full"></div>
            <div className="h-4 w-40 bg-slate-200 rounded"></div>
            <div className="h-8 w-20 bg-emerald-200 rounded"></div>
          </div>
        ))}
      </div>
    </div>
    {/* Related Assets section */}
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 bg-indigo-200 rounded"></div>
          <div className="h-6 w-36 bg-slate-200 rounded"></div>
          <div className="h-5 w-28 bg-slate-200 rounded-full"></div>
        </div>
        <div className="h-9 w-24 bg-indigo-200 rounded"></div>
      </div>
      <div className="h-14 bg-blue-50 border border-blue-100 rounded-lg"></div>
      <div className="bg-slate-100 rounded-lg p-4 h-44"></div>
    </div>
    <span className="sr-only">Loading entities…</span>
  </div>
);

const EntityManagement = ({ isReadOnly, handleReadOnlyAction }) => {
  const [entities, setEntities] = useState([]);
  const [relatedAssets, setRelatedAssets] = useState([]);
  const [entityInvestments, setEntityInvestments] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showEntityModal, setShowEntityModal] = useState(false);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [entitiesRes, assetsRes] = await Promise.all([
        fetchWithAuth(`${BACKEND_URL}/api/entities`),
        fetchWithAuth(`${BACKEND_URL}/api/related-assets`)
      ]);
      if (!entitiesRes.ok || !assetsRes.ok) throw new Error('Failed to load data');
      const entitiesData = await entitiesRes.json();
      const assetsData = await assetsRes.json();
      setEntities(entitiesData);
      setRelatedAssets(assetsData);
      const investmentData = {};
      for (const entity of entitiesData) {
        const res = await fetchWithAuth(`${BACKEND_URL}/api/entities/${entity.entity_id}/investments`);
        if (res.ok) {
          const data = await res.json();
          investmentData[entity.entity_id] = { count: data.investment_count, value: data.total_value };
        }
      }
      setEntityInvestments(investmentData);
    } catch (error) {
      toast.error('Failed to load entities');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEntity = async (entityId) => {
    if (isReadOnly) { handleReadOnlyAction(); return; }
    const count = entityInvestments[entityId]?.count || 0;
    if (count > 0) { toast.error(`Cannot delete entity with ${count} linked investment(s). Delete or reassign them first.`); return; }
    if (!window.confirm('Are you sure you want to delete this entity?')) return;
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/entities/${entityId}`, { method: 'DELETE' });
      if (!response.ok) { const error = await response.json(); throw new Error(error.detail || 'Failed to delete entity'); }
      toast.success('Entity deleted successfully'); loadData();
    } catch (error) { toast.error(error.message); }
  };

  const handleDeleteAsset = async (assetId) => {
    if (isReadOnly) { handleReadOnlyAction(); return; }
    if (!window.confirm('Are you sure you want to delete this related asset?')) return;
    try {
      const response = await fetchWithAuth(`${BACKEND_URL}/api/related-assets/${assetId}`, { method: 'DELETE' });
      if (!response.ok) { const error = await response.json(); throw new Error(error.detail || 'Failed to delete asset'); }
      toast.success('Related asset deleted successfully'); loadData();
    } catch (error) { toast.error(error.message); }
  };

  if (isLoading) {
    return <EntityManagementSkeleton />;
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl sm:text-3xl font-serif font-bold text-slate-900 mb-2">Entity Management</h2>
        <p className="text-sm sm:text-base text-slate-600">Manage your holding companies and related assets (trusts, foundations)</p>
      </div>

      {/* Holding Entities */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
            <h3 className="text-lg sm:text-2xl font-serif font-bold text-slate-900">Holding Entities</h3>
          </div>
          <Button onClick={() => isReadOnly ? handleReadOnlyAction() : (setSelectedEntity(null), setShowEntityModal(true))} className="bg-emerald-600 hover:bg-emerald-700">
            <PlusCircle className="w-4 h-4 mr-2" /> Add Entity
          </Button>
        </div>
        {entities.length === 0 && (
          <div className="flex items-center gap-2" data-testid="holding-entities-empty-heading">
            <Building2 className="w-5 h-5 text-emerald-600" />
            <p className="text-base sm:text-lg font-semibold text-slate-900">No Holding Entities Yet</p>
          </div>
        )}
        <Card className="bg-emerald-50 border-emerald-200" data-testid="holding-entities-note">
          <CardContent className="py-3 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-900">
              <strong>Note:</strong> Holding entities (companies, LLCs, SPVs, family offices) are legal vehicles that own investments on your behalf. Use them to track <strong>indirect</strong> holdings separately from investments you hold personally — useful for tax, succession & reporting clarity.
            </p>
          </CardContent>
        </Card>
        <EntityCardGrid
          entities={entities} entityInvestments={entityInvestments}
          isReadOnly={isReadOnly} handleReadOnlyAction={handleReadOnlyAction}
          onEdit={(e) => { setSelectedEntity(e); setShowEntityModal(true); }}
          onDelete={handleDeleteEntity}
          onAdd={() => setShowEntityModal(true)}
        />
      </div>

      {/* Related Assets */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Scale className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
            <h3 className="text-lg sm:text-2xl font-serif font-bold text-slate-900">Related Assets</h3>
            <Badge variant="outline" className="text-xs">Memorandum Only</Badge>
          </div>
          <Button onClick={() => isReadOnly ? handleReadOnlyAction() : (setSelectedAsset(null), setShowAssetModal(true))} className="bg-indigo-600 hover:bg-indigo-700">
            <PlusCircle className="w-4 h-4 mr-2" /> Add Asset
          </Button>
        </div>
        {relatedAssets.length === 0 && (
          <div className="flex items-center gap-2" data-testid="related-assets-empty-heading">
            <Scale className="w-5 h-5 text-indigo-600" />
            <p className="text-base sm:text-lg font-semibold text-slate-900">No Related Assets Yet</p>
          </div>
        )}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-3 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900">
              <strong>Note:</strong> Related assets are wealth you hold for the benefit of <strong>third parties</strong> — typically your children, spouse, or other family members — through vehicles like trusts or foundations. Tracked for memorandum purposes only and <strong>NOT included</strong> in your total portfolio value.
            </p>
          </CardContent>
        </Card>
        <AssetCardGrid
          relatedAssets={relatedAssets}
          isReadOnly={isReadOnly} handleReadOnlyAction={handleReadOnlyAction}
          onEdit={(a) => { setSelectedAsset(a); setShowAssetModal(true); }}
          onDelete={handleDeleteAsset}
          onAdd={() => setShowAssetModal(true)}
        />
      </div>

      {showEntityModal && (
        <EntityModal entity={selectedEntity}
          onClose={() => { setShowEntityModal(false); setSelectedEntity(null); }}
          onSuccess={() => { setShowEntityModal(false); setSelectedEntity(null); loadData(); }}
        />
      )}
      {showAssetModal && (
        <AssetModal asset={selectedAsset}
          onClose={() => { setShowAssetModal(false); setSelectedAsset(null); }}
          onSuccess={() => { setShowAssetModal(false); setSelectedAsset(null); loadData(); }}
        />
      )}
    </div>
  );
};

export default EntityManagement;
